/**
 * Exercise Strategy Registry
 *
 * Central registry for exercise detection strategies.
 * Strategies register themselves when imported.
 */

const strategies = {};

/**
 * Register a strategy class
 * @param {typeof BaseExerciseStrategy} StrategyClass - Strategy class to register
 */
export function registerStrategy(StrategyClass) {
  const id = StrategyClass.id;
  if (!id) {
    throw new Error('Strategy class must have a static id property');
  }
  strategies[id] = StrategyClass;
}

/**
 * Create a strategy instance by exercise type ID
 * @param {string} exerciseType - Exercise type ID (e.g., 'skipping', 'pickleball')
 * @returns {BaseExerciseStrategy} Strategy instance
 */
export function createStrategy(exerciseType) {
  const StrategyClass = strategies[exerciseType];
  if (!StrategyClass) {
    const available = Object.keys(strategies).join(', ');
    throw new Error(`Unknown exercise type: ${exerciseType}. Available: ${available}`);
  }
  return new StrategyClass();
}

/**
 * Get all available strategies
 * @returns {Array<{id: string, displayName: string}>}
 */
export function getAvailableStrategies() {
  return Object.values(strategies).map(S => ({
    id: S.id,
    displayName: S.displayName,
    detectorType: S.detectorType,
    counterId: S.counterId
  }));
}

/**
 * Check if a strategy is registered
 * @param {string} exerciseType - Exercise type ID
 * @returns {boolean}
 */
export function hasStrategy(exerciseType) {
  return exerciseType in strategies;
}

/**
 * Get strategy class by ID (without instantiating)
 * @param {string} exerciseType - Exercise type ID
 * @returns {typeof BaseExerciseStrategy|null}
 */
export function getStrategyClass(exerciseType) {
  return strategies[exerciseType] || null;
}
